import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Plus, Edit2, Power, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AppHeader from '../components/AppHeader';
import Popover from '../components/Popover';
import Breadcrumb from '../components/Breadcrumb';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import {
  PageContainer, PageHeader, PageTitle, PageSubtitle,
  Card, Badge, Button, Input, Select, Label, FormGroup, Flex, Grid,
  Spinner, LoadingContainer, EmptyState, EmptyText,
} from '../components/ui';
import { theme } from '../styles/theme';
import api from '../services/api';

const breadcrumbItems = [
  { label: 'Libben Agents', icon: '🚀', path: '/sallus/dashboard' },
  { label: 'Task Templates', icon: '📋' }
];

const CATEGORIES = ['communication', 'monitoring', 'purchase', 'verification'];

export default function TaskTemplatesAdmin() {
  const { t }   = useTranslation('tasks');
  const toast   = useToast();
  const confirm = useConfirm();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editing, setEditing]     = useState(null); // null | {} | template

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/task-templates');
      setTemplates(res.data.templates || []);
    } catch { toast.error(t('loadError', { ns: 'common' })); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeactivate = async (tpl) => {
    const ok = await confirm({ title: t('templates.deactivateConfirm', { name: tpl.name }), danger: true });
    if (!ok) return;
    try {
      await api.delete(`/task-templates/${tpl.templateId}`);
      toast.success(t('saveSuccess', { ns: 'common' }));
      load();
    } catch { toast.error(t('saveError', { ns: 'common' })); }
  };

  const handleSave = async (data) => {
    try {
      if (data.templateId && templates.find(t => t.templateId === data.templateId)) {
        await api.put(`/task-templates/${data.templateId}`, data);
      } else {
        await api.post('/task-templates', data);
      }
      toast.success(t('saveSuccess', { ns: 'common' }));
      setEditing(null);
      load();
    } catch { toast.error(t('saveError', { ns: 'common' })); }
  };

  const byCategory = CATEGORIES.reduce((acc, cat) => {
    const items = templates.filter(tpl => tpl.category === cat);
    if (items.length > 0) acc[cat] = items;
    return acc;
  }, {});

  return (
    <>
      <AppHeader title={t('templates.title')} />
      <BreadcrumbBar><Breadcrumb items={breadcrumbItems} /></BreadcrumbBar>
      <PageContainer>
        <PageHeader>
          <div>
            <PageTitle>{t('templates.title')}</PageTitle>
            <PageSubtitle>{t('templates.subtitle')}</PageSubtitle>
          </div>
          <Button onClick={() => setEditing({
            templateId: '', name: '', description: '', category: 'communication', icon: '📋',
            defaultExpectation: { type: 'any_response', timeoutMinutes: 120 },
            defaultOnTimeout: { maxAttempts: 3, retryMessage: '' },
            defaultReporting: { mode: 'immediate' },
            defaultPriority: 'normal',
            tenantCustomizable: ['title', 'target', 'message', 'timeoutMinutes', 'reportTo']
          })}>
            <Plus size={14} /> {t('templates.newTemplate')}
          </Button>
        </PageHeader>

        {editing && (
          <TemplateForm
            template={editing}
            onSave={handleSave}
            onCancel={() => setEditing(null)}
            t={t}
          />
        )}

        {loading ? (
          <LoadingContainer><Spinner size="24px" /></LoadingContainer>
        ) : templates.length === 0 ? (
          <EmptyState><EmptyText>{t('templates.noTemplates')}</EmptyText></EmptyState>
        ) : (
          Object.entries(byCategory).map(([cat, items]) => (
            <Section key={cat}>
              <CatTitle>{t(`templates.category.${cat}`, { defaultValue: cat })}</CatTitle>
              <Grid min="300px">
                {items.map(tpl => (
                  <Card key={tpl.templateId} style={{ opacity: tpl.active === false ? 0.5 : 1 }}>
                    <Flex justify="space-between" style={{ marginBottom: theme.spacing[3] }}>
                      <Flex gap={theme.spacing[2]}>
                        <span style={{ fontSize: 24 }}>{tpl.icon}</span>
                        <div>
                          <TplName>{tpl.name}</TplName>
                          <TplDesc>{tpl.description}</TplDesc>
                        </div>
                      </Flex>
                      <Flex gap={theme.spacing[1]}>
                        <Button variant="ghost" size="sm" onClick={() => setEditing({ ...tpl })}>
                          <Edit2 size={13} />
                        </Button>
                        {tpl.active !== false && (
                          <Button variant="ghost" size="sm" onClick={() => handleDeactivate(tpl)}>
                            <Power size={13} />
                          </Button>
                        )}
                      </Flex>
                    </Flex>
                    <TplMeta>
                      <Badge variant="neutral">{tpl.defaultExpectation?.type}</Badge>
                      <span>⏱ {tpl.defaultExpectation?.timeoutMinutes || 120}min</span>
                      <span>🔄 {tpl.defaultOnTimeout?.maxAttempts || 3}x</span>
                      <span>📢 {tpl.defaultReporting?.mode}</span>
                    </TplMeta>
                  </Card>
                ))}
              </Grid>
            </Section>
          ))
        )}
      </PageContainer>
    </>
  );
}

// ── TemplateForm ──────────────────────────────────────────────────────────────

function TemplateForm({ template, onSave, onCancel, t }) {
  const [data, setData] = useState({ ...template });
  const [saving, setSaving] = useState(false);
  const set = (key, value) => setData(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!data.templateId || !data.name) return;
    setSaving(true);
    try { await onSave(data); }
    finally { setSaving(false); }
  };

  return (
    <FormCard>
      <FormTitle>{data.templateId && template.name ? t('templates.editTemplate', { name: template.name }) : t('templates.newTemplate')}</FormTitle>
      <FormGrid>
        <FormGroup>
          <LabelWithHelp>
            <Label style={{ margin: 0 }}>{t('templates.templateId')}</Label>
            <Popover content={t('tooltips.templateId')}><HelpCircle size={13} color={theme.colors.textMuted} /></Popover>
          </LabelWithHelp>
          <Input value={data.templateId} onChange={e => set('templateId', e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
            placeholder={t('templates.templateIdPlaceholder')} disabled={!!template.name} />
        </FormGroup>
        <FormGroup>
          <Label>{t('templates.templateName')}</Label>
          <Input value={data.name} onChange={e => set('name', e.target.value)} placeholder={t('templates.templateNamePlaceholder')} />
        </FormGroup>
        <FormGroup>
          <Label>{t('templates.templateIcon')}</Label>
          <Input value={data.icon} onChange={e => set('icon', e.target.value)} placeholder="💰" style={{ width: 80 }} />
        </FormGroup>
        <FormGroup>
          <Label>{t('templates.templateCategory')}</Label>
          <Select value={data.category} onChange={e => set('category', e.target.value)}>
            {CATEGORIES.map(c => <option key={c} value={c}>{t(`templates.category.${c}`, { defaultValue: c })}</option>)}
          </Select>
        </FormGroup>
      </FormGrid>

      <FormGroup>
        <Label>{t('templates.templateDesc')}</Label>
        <Input value={data.description} onChange={e => set('description', e.target.value)} placeholder={t('templates.templateDescPlaceholder')} />
      </FormGroup>

      <SubTitle>{t('templates.defaultObjective')}</SubTitle>
      <FormGroup>
        <LabelWithHelp>
          <Label style={{ margin: 0 }}>{t('templates.objectiveLabel')}</Label>
          <Popover content={t('tooltips.objective')}><HelpCircle size={13} color={theme.colors.textMuted} /></Popover>
        </LabelWithHelp>
        <Textarea rows={4} value={data.defaultObjective || ''} onChange={e => set('defaultObjective', e.target.value)}
          placeholder={t('templates.objectivePlaceholder')} />
        <HelperText>{t('templates.objectiveVarHint')}</HelperText>
      </FormGroup>

      <SubTitle>{t('templates.defaultParams')}</SubTitle>
      <FormGrid>
        <FormGroup>
          <LabelWithHelp>
            <Label style={{ margin: 0 }}>{t('templates.timeoutMinutes')}</Label>
            <Popover content={t('tooltips.timeout')}><HelpCircle size={13} color={theme.colors.textMuted} /></Popover>
          </LabelWithHelp>
          <Input type="number" value={data.defaultExpectation?.timeoutMinutes || 120}
            onChange={e => setData(prev => ({ ...prev, defaultExpectation: { ...prev.defaultExpectation, timeoutMinutes: parseInt(e.target.value) } }))} />
        </FormGroup>
        <FormGroup>
          <LabelWithHelp>
            <Label style={{ margin: 0 }}>{t('templates.maxAttempts')}</Label>
            <Popover content={t('tooltips.maxAttempts')}><HelpCircle size={13} color={theme.colors.textMuted} /></Popover>
          </LabelWithHelp>
          <Input type="number" min="1" max="20" value={data.defaultOnTimeout?.maxAttempts || 3}
            onChange={e => setData(prev => ({ ...prev, defaultOnTimeout: { ...prev.defaultOnTimeout, maxAttempts: parseInt(e.target.value) } }))} />
        </FormGroup>
        <FormGroup>
          <LabelWithHelp>
            <Label style={{ margin: 0 }}>{t('templates.defaultPriority')}</Label>
            <Popover content={t('tooltips.priority')}><HelpCircle size={13} color={theme.colors.textMuted} /></Popover>
          </LabelWithHelp>
          <Select value={data.defaultPriority || 'normal'} onChange={e => set('defaultPriority', e.target.value)}>
            {['normal', 'urgent', 'critical'].map(k => <option key={k} value={k}>{k}</option>)}
          </Select>
        </FormGroup>
      </FormGrid>

      <Flex gap={theme.spacing[3]} style={{ marginTop: theme.spacing[5] }}>
        <Button variant="secondary" onClick={onCancel}>{t('cancel', { ns: 'common' })}</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || !data.templateId || !data.name}>
          {saving ? <><Spinner size="14px" /> {t('saving', { ns: 'common' })}</> : t('save', { ns: 'common' })}
        </Button>
      </Flex>
    </FormCard>
  );
}

// ── Styled ────────────────────────────────────────────────────────────────────

const BreadcrumbBar = styled.div`background: ${theme.colors.white}; padding: 0 ${theme.spacing[6]}; border-bottom: 1px solid ${theme.colors.border};`;
const LabelWithHelp = styled.div`display: flex; align-items: center; gap: ${theme.spacing[1]};`;
const Section = styled.div`margin-bottom: ${theme.spacing[6]};`;
const CatTitle = styled.h3`font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textMuted}; text-transform: uppercase; letter-spacing: 0.05em; margin: 0 0 ${theme.spacing[3]} 0;`;
const TplName = styled.div`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary};`;
const TplDesc = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};`;
const TplMeta = styled.div`display: flex; align-items: center; gap: ${theme.spacing[2]}; font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; flex-wrap: wrap;`;
const FormCard = styled(Card)`margin-bottom: ${theme.spacing[6]}; border-left: 3px solid ${theme.colors.primary};`;
const FormTitle = styled.h3`font-size: ${theme.typography.base}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary}; margin: 0 0 ${theme.spacing[5]} 0;`;
const FormGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: ${theme.spacing[4]};`;
const SubTitle = styled.h4`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textSecondary}; text-transform: uppercase; letter-spacing: 0.05em; margin: ${theme.spacing[5]} 0 ${theme.spacing[3]} 0; border-top: 1px solid ${theme.colors.border}; padding-top: ${theme.spacing[4]};`;
const Textarea = styled.textarea`width: 100%; padding: ${theme.spacing[2]} ${theme.spacing[3]}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md}; font-size: ${theme.typography.sm}; font-family: inherit; resize: vertical; box-sizing: border-box; outline: none; &:focus { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 3px ${theme.colors.primaryLight}; }`;
const HelperText = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; margin-top: ${theme.spacing[1]};`;
